import type {
  CreateCustomerVisitSessionRequest,
  CustomerGroupBuyRequestMutationResult,
  CustomerSelfServiceMutationResult,
  CustomerVisitSessionReadModel,
  CustomerVisitSessionRevokeRequest,
  CustomerVisitSessionRevokeResult,
  SelfServiceCustomerReservationCancelRequest,
  SelfServiceCustomerReservationCreateRequest,
  SelfServiceGroupBuyRequestCreateRequest,
} from "@billiard/contracts";
import {
  isCreateCustomerVisitSessionRequest,
  isCustomerGroupBuyRequestMutationResult,
  isCustomerSelfServiceMutationResult,
  isCustomerVisitSessionReadModel,
  isCustomerVisitSessionRevokeRequest,
  isCustomerVisitSessionRevokeResult,
  isSelfServiceCustomerReservationCancelRequest,
  isSelfServiceCustomerReservationCreateRequest,
  isSelfServiceGroupBuyRequestCreateRequest,
} from "@billiard/contracts";

const stableIdPattern = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;
const customerCsrfTokenPattern = /^[A-Za-z0-9_-]{43}$/u;

export const CUSTOMER_SELF_SERVICE_SESSION_CREATION_RECOVERY_STORAGE_KEY =
  "billiard-erp:customer-self-service-session-creation-recovery:v1";
export const CUSTOMER_SELF_SERVICE_SESSION_STORAGE_KEY =
  "billiard-erp:customer-self-service-session:v1";
export const CUSTOMER_SELF_SERVICE_MUTATION_RECOVERY_STORAGE_KEY =
  "billiard-erp:customer-self-service-mutation-recovery:v1";
export const CUSTOMER_SELF_SERVICE_REVOKE_RECOVERY_STORAGE_KEY =
  "billiard-erp:customer-self-service-revoke-recovery:v1";
export const CUSTOMER_GROUP_BUY_REQUEST_RECOVERY_STORAGE_KEY =
  "billiard-erp:customer-group-buy-request-recovery:v1";

export interface CustomerSelfServiceIdentity {
  tenantId: string;
  storeId: string;
  visitId: string;
}

export interface CustomerSelfServiceSetupScope {
  tenantId: string;
  storeId: string;
}

export interface CustomerSelfServiceSessionState {
  session: CustomerVisitSessionReadModel;
  csrfToken: string;
}

export type CustomerSelfServiceRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN" | "QUARANTINED";

export interface CustomerVisitSessionCreationRecovery {
  ownerSetupKey: string;
  request: CreateCustomerVisitSessionRequest;
  state: CustomerSelfServiceRecoveryState;
}

export type CustomerSelfServiceMutationCommand =
  | {
      operation: "CREATE";
      identity: CustomerSelfServiceIdentity;
      request: SelfServiceCustomerReservationCreateRequest;
    }
  | {
      operation: "CANCEL";
      identity: CustomerSelfServiceIdentity;
      reservationId: string;
      request: SelfServiceCustomerReservationCancelRequest;
    };

export interface CustomerSelfServiceMutationRecovery {
  ownerIdentityKey: string;
  command: CustomerSelfServiceMutationCommand;
  state: CustomerSelfServiceRecoveryState;
}

export interface CustomerVisitSessionRevokeRecovery {
  ownerIdentityKey: string;
  identity: CustomerSelfServiceIdentity;
  request: CustomerVisitSessionRevokeRequest;
  state: CustomerSelfServiceRecoveryState;
}

export interface CustomerGroupBuyRequestRecovery {
  ownerIdentityKey: string;
  identity: CustomerSelfServiceIdentity;
  request: SelfServiceGroupBuyRequestCreateRequest;
  state: CustomerSelfServiceRecoveryState;
}

export type CustomerSelfServiceRecoveryView<T> =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: T }
  | { status: "OTHER_IDENTITY" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

export type CustomerSelfServiceSessionView =
  | { status: "NONE" }
  | { status: "OWNED"; sessionState: CustomerSelfServiceSessionState }
  | { status: "OTHER_IDENTITY" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

interface MutationRecoveryEnvelope {
  CREATE: CustomerSelfServiceMutationRecovery | null;
  CANCEL: CustomerSelfServiceMutationRecovery | null;
}

let sessionCreationRecovery: CustomerVisitSessionCreationRecovery | null = null;
let sessionCreationHydrated = false;
let sessionCreationStorageUnavailable = false;
let sessionCreationDispatching: CustomerVisitSessionCreationRecovery | null = null;

let sessionState: CustomerSelfServiceSessionState | null = null;
let sessionHydrated = false;
let sessionStorageUnavailable = false;

let mutationRecoveries: MutationRecoveryEnvelope = { CREATE: null, CANCEL: null };
let mutationsHydrated = false;
let mutationStorageUnavailable = false;
const dispatchingMutations = new Set<CustomerSelfServiceMutationRecovery>();

let revokeRecovery: CustomerVisitSessionRevokeRecovery | null = null;
let revokeHydrated = false;
let revokeStorageUnavailable = false;
let revokeDispatching: CustomerVisitSessionRevokeRecovery | null = null;

let groupBuyRequestRecovery: CustomerGroupBuyRequestRecovery | null = null;
let groupBuyRequestHydrated = false;
let groupBuyRequestStorageUnavailable = false;
let groupBuyRequestDispatching: CustomerGroupBuyRequestRecovery | null = null;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child);
  }
  return value;
}

function immutableClone<T>(value: T): T {
  return deepFreeze(structuredClone(value));
}

function storage(): Storage | null {
  if (typeof window === "undefined" || typeof localStorage === "undefined") return null;
  try {
    const probe = localStorage.length;
    void probe;
    return localStorage;
  } catch {
    return null;
  }
}

function writeVerified(key: string, value: unknown): boolean {
  const target = storage();
  if (!target) return false;
  try {
    const serialized = JSON.stringify(value);
    target.setItem(key, serialized);
    return target.getItem(key) === serialized;
  } catch {
    return false;
  }
}

function removeVerified(key: string): boolean {
  const target = storage();
  if (!target) return false;
  try {
    target.removeItem(key);
    return target.getItem(key) === null;
  } catch {
    return false;
  }
}

function readStored(key: string): { ok: true; value: unknown | null } | { ok: false } {
  const target = storage();
  if (!target) return { ok: false };
  try {
    const serialized = target.getItem(key);
    if (serialized === null) return { ok: true, value: null };
    return { ok: true, value: JSON.parse(serialized) as unknown };
  } catch {
    return { ok: false };
  }
}

function setupScopeIsValid(value: unknown): value is CustomerSelfServiceSetupScope {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["tenantId", "storeId"]) &&
    typeof value.tenantId === "string" &&
    stableIdPattern.test(value.tenantId) &&
    typeof value.storeId === "string" &&
    stableIdPattern.test(value.storeId)
  );
}

function identityIsValid(value: unknown): value is CustomerSelfServiceIdentity {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["tenantId", "storeId", "visitId"]) &&
    typeof value.tenantId === "string" &&
    stableIdPattern.test(value.tenantId) &&
    typeof value.storeId === "string" &&
    stableIdPattern.test(value.storeId) &&
    typeof value.visitId === "string" &&
    stableIdPattern.test(value.visitId)
  );
}

export function customerSelfServiceSetupKey(scope: CustomerSelfServiceSetupScope): string {
  return JSON.stringify([scope.tenantId, scope.storeId]);
}

export function customerSelfServiceIdentityKey(identity: CustomerSelfServiceIdentity): string {
  return JSON.stringify([identity.tenantId, identity.storeId, identity.visitId]);
}

function sessionIdentity(session: CustomerVisitSessionReadModel): CustomerSelfServiceIdentity {
  return {
    tenantId: session.tenantId,
    storeId: session.storeId,
    visitId: session.visitId,
  };
}

function expectedMaskedMobile(mobile: string): string {
  return `${mobile.slice(0, 3)}****${mobile.slice(-4)}`;
}

function creationRecoveryIsValid(value: unknown): value is CustomerVisitSessionCreationRecovery {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["ownerSetupKey", "request", "state"]) &&
    typeof value.ownerSetupKey === "string" &&
    isCreateCustomerVisitSessionRequest(value.request) &&
    value.ownerSetupKey === customerSelfServiceSetupKey(value.request) &&
    (value.state === "CLAIMED" || value.state === "OUTCOME_UNKNOWN")
  );
}

function hydrateSessionCreation(): boolean {
  if (sessionCreationHydrated) return !sessionCreationStorageUnavailable;
  sessionCreationHydrated = true;
  const stored = readStored(CUSTOMER_SELF_SERVICE_SESSION_CREATION_RECOVERY_STORAGE_KEY);
  if (!stored.ok) {
    sessionCreationStorageUnavailable = true;
    return false;
  }
  if (stored.value === null) return true;
  if (
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "recovery"]) ||
    stored.value.version !== 1 ||
    !creationRecoveryIsValid(stored.value.recovery)
  ) {
    sessionCreationStorageUnavailable = true;
    return false;
  }
  const restored = immutableClone({
    ...stored.value.recovery,
    state: "OUTCOME_UNKNOWN" as const,
  });
  if (
    stored.value.recovery.state === "CLAIMED" &&
    !writeVerified(CUSTOMER_SELF_SERVICE_SESSION_CREATION_RECOVERY_STORAGE_KEY, {
      version: 1,
      recovery: restored,
    })
  ) {
    sessionCreationStorageUnavailable = true;
    return false;
  }
  sessionCreationRecovery = restored;
  return true;
}

export function claimCustomerVisitSessionCreationRecovery(
  request: CreateCustomerVisitSessionRequest,
): CustomerSelfServiceRecoveryView<CustomerVisitSessionCreationRecovery> {
  if (!isCreateCustomerVisitSessionRequest(request) || !hydrateSessionCreation()) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const ownerSetupKey = customerSelfServiceSetupKey(request);
  if (sessionCreationRecovery) {
    return sessionCreationRecovery.ownerSetupKey === ownerSetupKey
      ? { status: "OWNED", recovery: sessionCreationRecovery }
      : { status: "OTHER_IDENTITY" };
  }
  const claimed = immutableClone({ ownerSetupKey, request, state: "CLAIMED" as const });
  if (
    !writeVerified(CUSTOMER_SELF_SERVICE_SESSION_CREATION_RECOVERY_STORAGE_KEY, {
      version: 1,
      recovery: claimed,
    })
  ) {
    sessionCreationStorageUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  sessionCreationRecovery = claimed;
  return { status: "OWNED", recovery: sessionCreationRecovery };
}

export function readCustomerVisitSessionCreationRecovery(
  scope: CustomerSelfServiceSetupScope,
): CustomerSelfServiceRecoveryView<CustomerVisitSessionCreationRecovery> {
  if (!setupScopeIsValid(scope) || !hydrateSessionCreation()) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  if (!sessionCreationRecovery) return { status: "NONE" };
  return sessionCreationRecovery.ownerSetupKey === customerSelfServiceSetupKey(scope)
    ? { status: "OWNED", recovery: sessionCreationRecovery }
    : { status: "OTHER_IDENTITY" };
}

export function beginCustomerVisitSessionCreationDispatch(
  recovery: CustomerVisitSessionCreationRecovery,
): boolean {
  if (
    !hydrateSessionCreation() ||
    recovery !== sessionCreationRecovery ||
    !sessionCreationStorageStillMatches()
  ) {
    return false;
  }
  if (sessionCreationDispatching) return false;
  sessionCreationDispatching = recovery;
  return true;
}

function sessionCreationStorageStillMatches(): boolean {
  const stored = readStored(CUSTOMER_SELF_SERVICE_SESSION_CREATION_RECOVERY_STORAGE_KEY);
  if (
    !stored.ok ||
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "recovery"]) ||
    stored.value.version !== 1 ||
    !creationRecoveryIsValid(stored.value.recovery) ||
    !sessionCreationRecovery ||
    JSON.stringify(stored.value.recovery) !== JSON.stringify(sessionCreationRecovery)
  ) {
    sessionCreationStorageUnavailable = true;
    return false;
  }
  return true;
}

export function finishCustomerVisitSessionCreationDispatch(
  recovery: CustomerVisitSessionCreationRecovery,
): boolean {
  if (sessionCreationDispatching !== recovery) return false;
  sessionCreationDispatching = null;
  return true;
}

function creationRecoveriesMatch(
  left: CustomerVisitSessionCreationRecovery,
  right: CustomerVisitSessionCreationRecovery,
): boolean {
  return (
    left.ownerSetupKey === right.ownerSetupKey &&
    JSON.stringify(left.request) === JSON.stringify(right.request)
  );
}

export function markCustomerVisitSessionCreationOutcomeUnknown(
  recovery: CustomerVisitSessionCreationRecovery,
): boolean {
  if (
    !hydrateSessionCreation() ||
    !sessionCreationRecovery ||
    !creationRecoveriesMatch(recovery, sessionCreationRecovery)
  ) {
    return false;
  }
  const unknown = immutableClone({ ...recovery, state: "OUTCOME_UNKNOWN" as const });
  if (
    !writeVerified(CUSTOMER_SELF_SERVICE_SESSION_CREATION_RECOVERY_STORAGE_KEY, {
      version: 1,
      recovery: unknown,
    })
  ) {
    sessionCreationStorageUnavailable = true;
    return false;
  }
  sessionCreationRecovery = unknown;
  sessionCreationDispatching = null;
  return true;
}

export function clearCustomerVisitSessionCreationRecovery(
  recovery: CustomerVisitSessionCreationRecovery,
): boolean {
  if (
    !hydrateSessionCreation() ||
    !sessionCreationRecovery ||
    !creationRecoveriesMatch(recovery, sessionCreationRecovery) ||
    !removeSessionCreationSecret()
  ) {
    return false;
  }
  return true;
}

function sessionStateIsValid(value: unknown): value is CustomerSelfServiceSessionState {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["session", "csrfToken"]) &&
    isCustomerVisitSessionReadModel(value.session) &&
    value.session.status === "ACTIVE" &&
    typeof value.csrfToken === "string" &&
    customerCsrfTokenPattern.test(value.csrfToken)
  );
}

function hydrateSession(): boolean {
  if (sessionHydrated) return !sessionStorageUnavailable;
  const stored = readStored(CUSTOMER_SELF_SERVICE_SESSION_STORAGE_KEY);
  if (!stored.ok) {
    sessionStorageUnavailable = true;
    return false;
  }
  if (stored.value === null) {
    sessionState = null;
    sessionHydrated = true;
    sessionStorageUnavailable = false;
    return true;
  }
  if (
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "identityKey", "sessionState"]) ||
    stored.value.version !== 1 ||
    typeof stored.value.identityKey !== "string" ||
    !sessionStateIsValid(stored.value.sessionState) ||
    stored.value.identityKey !==
      customerSelfServiceIdentityKey(sessionIdentity(stored.value.sessionState.session))
  ) {
    sessionStorageUnavailable = true;
    return false;
  }
  const restored = immutableClone(stored.value.sessionState);
  if (!finalizePersistedSessionCreation(restored)) {
    sessionState = null;
    sessionHydrated = false;
    sessionStorageUnavailable = false;
    return false;
  }
  sessionState = restored;
  sessionHydrated = true;
  sessionStorageUnavailable = false;
  return true;
}

export function readCustomerSelfServiceSession(
  expectedIdentity?: CustomerSelfServiceIdentity,
): CustomerSelfServiceSessionView {
  if ((expectedIdentity !== undefined && !identityIsValid(expectedIdentity)) || !hydrateSession()) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  if (!sessionState) return { status: "NONE" };
  if (
    expectedIdentity &&
    customerSelfServiceIdentityKey(expectedIdentity) !==
      customerSelfServiceIdentityKey(sessionIdentity(sessionState.session))
  ) {
    return { status: "OTHER_IDENTITY" };
  }
  return { status: "OWNED", sessionState };
}

function removeSessionCreationSecret(): boolean {
  const removed = removeVerified(CUSTOMER_SELF_SERVICE_SESSION_CREATION_RECOVERY_STORAGE_KEY);
  if (removed) {
    sessionCreationRecovery = null;
    sessionCreationHydrated = true;
    sessionCreationStorageUnavailable = false;
    sessionCreationDispatching = null;
  } else {
    sessionCreationStorageUnavailable = true;
  }
  return removed;
}

function creationRecoveryMatchesSessionState(
  recovery: CustomerVisitSessionCreationRecovery,
  state: CustomerSelfServiceSessionState,
): boolean {
  return (
    recovery.request.tenantId === state.session.tenantId &&
    recovery.request.storeId === state.session.storeId &&
    recovery.request.customerName === state.session.customerName &&
    expectedMaskedMobile(recovery.request.customerMobile) === state.session.customerMobileMasked &&
    recovery.request.csrfToken === state.csrfToken
  );
}

function finalizePersistedSessionCreation(state: CustomerSelfServiceSessionState): boolean {
  const stored = readStored(CUSTOMER_SELF_SERVICE_SESSION_CREATION_RECOVERY_STORAGE_KEY);
  if (!stored.ok) {
    sessionCreationStorageUnavailable = true;
    return false;
  }
  if (stored.value === null) {
    sessionCreationRecovery = null;
    sessionCreationHydrated = true;
    sessionCreationStorageUnavailable = false;
    sessionCreationDispatching = null;
    return true;
  }
  if (
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "recovery"]) ||
    stored.value.version !== 1 ||
    !creationRecoveryIsValid(stored.value.recovery) ||
    !creationRecoveryMatchesSessionState(stored.value.recovery, state)
  ) {
    sessionCreationStorageUnavailable = true;
    return false;
  }
  sessionCreationRecovery = immutableClone(stored.value.recovery);
  sessionCreationHydrated = true;
  return removeSessionCreationSecret();
}

export function storeCustomerSelfServiceSession(
  session: CustomerVisitSessionReadModel,
  csrfToken: string,
): boolean {
  if (
    !isCustomerVisitSessionReadModel(session) ||
    session.status !== "ACTIVE" ||
    !customerCsrfTokenPattern.test(csrfToken)
  ) {
    return false;
  }
  if (!hydrateRevoke() || revokeRecovery !== null) return false;
  if (!hydrateMutations() || anyMutationRecovery()) return false;
  if (!hydrateGroupBuyRequestRecovery() || groupBuyRequestRecovery !== null) return false;
  if (!removeVerified(CUSTOMER_SELF_SERVICE_MUTATION_RECOVERY_STORAGE_KEY)) {
    mutationStorageUnavailable = true;
    return false;
  }
  mutationRecoveries = { CREATE: null, CANCEL: null };
  mutationsHydrated = true;
  dispatchingMutations.clear();
  if (!removeVerified(CUSTOMER_GROUP_BUY_REQUEST_RECOVERY_STORAGE_KEY)) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  groupBuyRequestRecovery = null;
  groupBuyRequestHydrated = true;
  groupBuyRequestDispatching = null;

  const next = immutableClone({ session, csrfToken });
  const identityKey = customerSelfServiceIdentityKey(sessionIdentity(session));
  if (
    !writeVerified(CUSTOMER_SELF_SERVICE_SESSION_STORAGE_KEY, {
      version: 1,
      identityKey,
      sessionState: next,
    })
  ) {
    sessionStorageUnavailable = true;
    sessionState = null;
    sessionHydrated = false;
    return false;
  }
  if (!finalizePersistedSessionCreation(next)) {
    // The public session and the exact creation command intentionally remain
    // durable together. Until a later read verifies secret cleanup, callers
    // cannot observe or operate on the session.
    sessionState = null;
    sessionHydrated = false;
    sessionStorageUnavailable = false;
    return false;
  }
  sessionState = next;
  sessionHydrated = true;
  sessionStorageUnavailable = false;
  return true;
}

export function completeCustomerVisitSessionCreation(
  recovery: CustomerVisitSessionCreationRecovery,
  createdSession: CustomerVisitSessionReadModel,
): boolean {
  if (
    !hydrateSessionCreation() ||
    !sessionCreationRecovery ||
    !creationRecoveriesMatch(recovery, sessionCreationRecovery) ||
    !isCustomerVisitSessionReadModel(createdSession) ||
    createdSession.status !== "ACTIVE" ||
    createdSession.tenantId !== recovery.request.tenantId ||
    createdSession.storeId !== recovery.request.storeId ||
    createdSession.customerName !== recovery.request.customerName ||
    createdSession.customerMobileMasked !== expectedMaskedMobile(recovery.request.customerMobile)
  ) {
    return false;
  }
  return storeCustomerSelfServiceSession(createdSession, recovery.request.csrfToken);
}

function mutationCommandIsValid(value: unknown): value is CustomerSelfServiceMutationCommand {
  if (!isRecord(value) || !identityIsValid(value.identity)) return false;
  if (value.operation === "CREATE") {
    return (
      hasExactKeys(value, ["operation", "identity", "request"]) &&
      isSelfServiceCustomerReservationCreateRequest(value.request)
    );
  }
  return (
    value.operation === "CANCEL" &&
    hasExactKeys(value, ["operation", "identity", "reservationId", "request"]) &&
    typeof value.reservationId === "string" &&
    stableIdPattern.test(value.reservationId) &&
    isSelfServiceCustomerReservationCancelRequest(value.request)
  );
}

function mutationRecoveryIsValid(
  value: unknown,
  operation: "CREATE" | "CANCEL",
): value is CustomerSelfServiceMutationRecovery {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["ownerIdentityKey", "command", "state"]) &&
    typeof value.ownerIdentityKey === "string" &&
    mutationCommandIsValid(value.command) &&
    value.command.operation === operation &&
    value.ownerIdentityKey === customerSelfServiceIdentityKey(value.command.identity) &&
    (value.state === "CLAIMED" ||
      value.state === "OUTCOME_UNKNOWN" ||
      value.state === "QUARANTINED")
  );
}

function mutationEnvelopeIsValid(value: unknown): value is MutationRecoveryEnvelope {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["CREATE", "CANCEL"]) &&
    (value.CREATE === null || mutationRecoveryIsValid(value.CREATE, "CREATE")) &&
    (value.CANCEL === null || mutationRecoveryIsValid(value.CANCEL, "CANCEL"))
  );
}

function persistMutationRecoveries(envelope: MutationRecoveryEnvelope): boolean {
  if (envelope.CREATE === null && envelope.CANCEL === null) {
    return removeVerified(CUSTOMER_SELF_SERVICE_MUTATION_RECOVERY_STORAGE_KEY);
  }
  return writeVerified(CUSTOMER_SELF_SERVICE_MUTATION_RECOVERY_STORAGE_KEY, {
    version: 1,
    recoveries: envelope,
  });
}

function hydrateMutations(): boolean {
  if (mutationsHydrated) return !mutationStorageUnavailable;
  mutationsHydrated = true;
  const stored = readStored(CUSTOMER_SELF_SERVICE_MUTATION_RECOVERY_STORAGE_KEY);
  if (!stored.ok) {
    mutationStorageUnavailable = true;
    return false;
  }
  if (stored.value === null) return true;
  if (
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "recoveries"]) ||
    stored.value.version !== 1 ||
    !mutationEnvelopeIsValid(stored.value.recoveries)
  ) {
    mutationStorageUnavailable = true;
    return false;
  }
  const persisted = stored.value.recoveries;
  const restored: MutationRecoveryEnvelope = {
    CREATE:
      persisted.CREATE === null
        ? null
        : immutableClone({
            ...persisted.CREATE,
            state:
              persisted.CREATE.state === "CLAIMED"
                ? ("OUTCOME_UNKNOWN" as const)
                : persisted.CREATE.state,
          }),
    CANCEL:
      persisted.CANCEL === null
        ? null
        : immutableClone({
            ...persisted.CANCEL,
            state:
              persisted.CANCEL.state === "CLAIMED"
                ? ("OUTCOME_UNKNOWN" as const)
                : persisted.CANCEL.state,
          }),
  };
  if (
    (persisted.CREATE?.state === "CLAIMED" || persisted.CANCEL?.state === "CLAIMED") &&
    !persistMutationRecoveries(restored)
  ) {
    mutationStorageUnavailable = true;
    return false;
  }
  mutationRecoveries = restored;
  return true;
}

export function claimCustomerSelfServiceMutationRecovery(
  command: CustomerSelfServiceMutationCommand,
): CustomerSelfServiceRecoveryView<CustomerSelfServiceMutationRecovery> {
  if (!mutationCommandIsValid(command) || !hydrateMutations()) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const ownerIdentityKey = customerSelfServiceIdentityKey(command.identity);
  const current = mutationRecoveries[command.operation];
  if (current) {
    return current.ownerIdentityKey === ownerIdentityKey
      ? { status: "OWNED", recovery: current }
      : { status: "OTHER_IDENTITY" };
  }
  const recovery = immutableClone({ ownerIdentityKey, command, state: "CLAIMED" as const });
  const next = { ...mutationRecoveries, [command.operation]: recovery };
  if (!persistMutationRecoveries(next)) {
    mutationStorageUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  mutationRecoveries = next;
  return { status: "OWNED", recovery };
}

export function readCustomerSelfServiceMutationRecovery(
  identity: CustomerSelfServiceIdentity,
  operation: "CREATE" | "CANCEL",
): CustomerSelfServiceRecoveryView<CustomerSelfServiceMutationRecovery> {
  if (!identityIsValid(identity) || !hydrateMutations()) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const current = mutationRecoveries[operation];
  if (!current) return { status: "NONE" };
  return current.ownerIdentityKey === customerSelfServiceIdentityKey(identity)
    ? { status: "OWNED", recovery: current }
    : { status: "OTHER_IDENTITY" };
}

function mutationStorageStillMatches(): boolean {
  const stored = readStored(CUSTOMER_SELF_SERVICE_MUTATION_RECOVERY_STORAGE_KEY);
  if (
    !stored.ok ||
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "recoveries"]) ||
    stored.value.version !== 1 ||
    !mutationEnvelopeIsValid(stored.value.recoveries)
  ) {
    mutationStorageUnavailable = true;
    return false;
  }
  return JSON.stringify(stored.value.recoveries) === JSON.stringify(mutationRecoveries);
}

export function beginCustomerSelfServiceMutationDispatch(
  recovery: CustomerSelfServiceMutationRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateMutations() ||
    recovery !== mutationRecoveries[recovery.command.operation] ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state === "QUARANTINED" ||
    !mutationStorageStillMatches() ||
    dispatchingMutations.has(recovery)
  ) {
    return false;
  }
  dispatchingMutations.add(recovery);
  return true;
}

export function finishCustomerSelfServiceMutationDispatch(
  recovery: CustomerSelfServiceMutationRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    !dispatchingMutations.has(recovery)
  ) {
    return false;
  }
  dispatchingMutations.delete(recovery);
  return true;
}

export function markCustomerSelfServiceMutationOutcomeUnknown(
  recovery: CustomerSelfServiceMutationRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateMutations() ||
    recovery !== mutationRecoveries[recovery.command.operation] ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state === "QUARANTINED"
  ) {
    return false;
  }
  const unknown = immutableClone({ ...recovery, state: "OUTCOME_UNKNOWN" as const });
  const next = { ...mutationRecoveries, [recovery.command.operation]: unknown };
  if (!persistMutationRecoveries(next)) {
    mutationStorageUnavailable = true;
    return false;
  }
  dispatchingMutations.delete(recovery);
  mutationRecoveries = next;
  return true;
}

export function quarantineCustomerSelfServiceMutationRecovery(
  recovery: CustomerSelfServiceMutationRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateMutations() ||
    recovery !== mutationRecoveries[recovery.command.operation] ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity)
  ) {
    return false;
  }
  if (recovery.state === "QUARANTINED") return true;
  const quarantined = immutableClone({ ...recovery, state: "QUARANTINED" as const });
  const next = { ...mutationRecoveries, [recovery.command.operation]: quarantined };
  if (!persistMutationRecoveries(next)) {
    mutationStorageUnavailable = true;
    return false;
  }
  dispatchingMutations.delete(recovery);
  mutationRecoveries = next;
  return true;
}

function removeCurrentMutationRecovery(recovery: CustomerSelfServiceMutationRecovery): boolean {
  const next = { ...mutationRecoveries, [recovery.command.operation]: null };
  if (!persistMutationRecoveries(next)) {
    mutationStorageUnavailable = true;
    return false;
  }
  dispatchingMutations.delete(recovery);
  mutationRecoveries = next;
  return true;
}

export function clearCustomerSelfServiceMutationRecovery(
  recovery: CustomerSelfServiceMutationRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateMutations() ||
    recovery !== mutationRecoveries[recovery.command.operation] ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state !== "CLAIMED"
  ) {
    return false;
  }
  return removeCurrentMutationRecovery(recovery);
}

export function customerSelfServiceMutationResultMatchesRecovery(
  recovery: CustomerSelfServiceMutationRecovery,
  result: unknown,
): result is CustomerSelfServiceMutationResult {
  if (
    !mutationRecoveryIsValid(recovery, recovery.command.operation) ||
    recovery.state === "QUARANTINED"
  ) {
    return false;
  }
  if (
    !isCustomerSelfServiceMutationResult(result) ||
    result.operation !== recovery.command.operation
  ) {
    return false;
  }
  if (recovery.command.operation === "CREATE") {
    const request = recovery.command.request;
    const requestedEndAt = new Date(
      Date.parse(request.requestedStartAt) + request.durationMinutes * 60_000,
    ).toISOString();
    return (
      result.reservation.partySize === request.partySize &&
      result.reservation.requestedStartAt === request.requestedStartAt &&
      result.reservation.requestedEndAt === requestedEndAt &&
      result.reservation.note === request.note
    );
  }
  return (
    result.reservation.reservationId === recovery.command.reservationId &&
    result.reservation.reservationVersion ===
      recovery.command.request.expectedReservationVersion + 1
  );
}

export function customerSelfServiceMutationRecoveryCanQuery(
  recovery: CustomerSelfServiceMutationRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  return (
    identityIsValid(identity) &&
    hydrateMutations() &&
    recovery === mutationRecoveries[recovery.command.operation] &&
    recovery.ownerIdentityKey === customerSelfServiceIdentityKey(identity) &&
    recovery.state === "OUTCOME_UNKNOWN" &&
    mutationStorageStillMatches()
  );
}

export function customerSelfServiceMutationCanRetryAfterNotFound(
  recovery: CustomerSelfServiceMutationRecovery,
  identity: CustomerSelfServiceIdentity,
  lookupResult: CustomerSelfServiceMutationResult | null,
): boolean {
  return lookupResult === null && customerSelfServiceMutationRecoveryCanQuery(recovery, identity);
}

export function clearCustomerSelfServiceMutationAfterNotFound(
  recovery: CustomerSelfServiceMutationRecovery,
  identity: CustomerSelfServiceIdentity,
  lookupResult: CustomerSelfServiceMutationResult | null,
): boolean {
  return (
    customerSelfServiceMutationCanRetryAfterNotFound(recovery, identity, lookupResult) &&
    removeCurrentMutationRecovery(recovery)
  );
}

export function completeCustomerSelfServiceMutationRecovery(
  recovery: CustomerSelfServiceMutationRecovery,
  identity: CustomerSelfServiceIdentity,
  result: unknown,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateMutations() ||
    recovery !== mutationRecoveries[recovery.command.operation] ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state === "QUARANTINED"
  ) {
    return false;
  }
  if (!customerSelfServiceMutationResultMatchesRecovery(recovery, result)) {
    quarantineCustomerSelfServiceMutationRecovery(recovery, identity);
    return false;
  }
  return removeCurrentMutationRecovery(recovery);
}

function groupBuyRequestRecoveryIsValid(value: unknown): value is CustomerGroupBuyRequestRecovery {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["ownerIdentityKey", "identity", "request", "state"]) &&
    typeof value.ownerIdentityKey === "string" &&
    identityIsValid(value.identity) &&
    value.ownerIdentityKey === customerSelfServiceIdentityKey(value.identity) &&
    isSelfServiceGroupBuyRequestCreateRequest(value.request) &&
    (value.state === "CLAIMED" ||
      value.state === "OUTCOME_UNKNOWN" ||
      value.state === "QUARANTINED")
  );
}

function persistGroupBuyRequestRecovery(recovery: CustomerGroupBuyRequestRecovery | null): boolean {
  if (recovery === null) return removeVerified(CUSTOMER_GROUP_BUY_REQUEST_RECOVERY_STORAGE_KEY);
  return writeVerified(CUSTOMER_GROUP_BUY_REQUEST_RECOVERY_STORAGE_KEY, {
    version: 1,
    recovery,
  });
}

function hydrateGroupBuyRequestRecovery(): boolean {
  if (groupBuyRequestHydrated) return !groupBuyRequestStorageUnavailable;
  groupBuyRequestHydrated = true;
  const stored = readStored(CUSTOMER_GROUP_BUY_REQUEST_RECOVERY_STORAGE_KEY);
  if (!stored.ok) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  if (stored.value === null) return true;
  if (
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "recovery"]) ||
    stored.value.version !== 1 ||
    !groupBuyRequestRecoveryIsValid(stored.value.recovery)
  ) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  const restored = immutableClone({
    ...stored.value.recovery,
    state:
      stored.value.recovery.state === "CLAIMED"
        ? ("OUTCOME_UNKNOWN" as const)
        : stored.value.recovery.state,
  });
  if (stored.value.recovery.state === "CLAIMED" && !persistGroupBuyRequestRecovery(restored)) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  groupBuyRequestRecovery = restored;
  return true;
}

function groupBuyRequestStorageStillMatches(): boolean {
  const stored = readStored(CUSTOMER_GROUP_BUY_REQUEST_RECOVERY_STORAGE_KEY);
  if (
    !stored.ok ||
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "recovery"]) ||
    stored.value.version !== 1 ||
    !groupBuyRequestRecoveryIsValid(stored.value.recovery) ||
    !groupBuyRequestRecovery ||
    JSON.stringify(stored.value.recovery) !== JSON.stringify(groupBuyRequestRecovery)
  ) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  return true;
}

export function claimCustomerGroupBuyRequestRecovery(
  identity: CustomerSelfServiceIdentity,
  request: SelfServiceGroupBuyRequestCreateRequest,
): CustomerSelfServiceRecoveryView<CustomerGroupBuyRequestRecovery> {
  if (
    !identityIsValid(identity) ||
    !isSelfServiceGroupBuyRequestCreateRequest(request) ||
    !hydrateGroupBuyRequestRecovery()
  ) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const ownerIdentityKey = customerSelfServiceIdentityKey(identity);
  if (groupBuyRequestRecovery) {
    return groupBuyRequestRecovery.ownerIdentityKey === ownerIdentityKey
      ? { status: "OWNED", recovery: groupBuyRequestRecovery }
      : { status: "OTHER_IDENTITY" };
  }
  const recovery = immutableClone({
    ownerIdentityKey,
    identity,
    request,
    state: "CLAIMED" as const,
  });
  if (!persistGroupBuyRequestRecovery(recovery)) {
    groupBuyRequestStorageUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  groupBuyRequestRecovery = recovery;
  return { status: "OWNED", recovery };
}

export function readCustomerGroupBuyRequestRecovery(
  identity: CustomerSelfServiceIdentity,
): CustomerSelfServiceRecoveryView<CustomerGroupBuyRequestRecovery> {
  if (!identityIsValid(identity) || !hydrateGroupBuyRequestRecovery()) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  if (!groupBuyRequestRecovery) return { status: "NONE" };
  return groupBuyRequestRecovery.ownerIdentityKey === customerSelfServiceIdentityKey(identity)
    ? { status: "OWNED", recovery: groupBuyRequestRecovery }
    : { status: "OTHER_IDENTITY" };
}

export function beginCustomerGroupBuyRequestDispatch(
  recovery: CustomerGroupBuyRequestRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateGroupBuyRequestRecovery() ||
    recovery !== groupBuyRequestRecovery ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state === "QUARANTINED" ||
    !groupBuyRequestStorageStillMatches() ||
    groupBuyRequestDispatching !== null
  ) {
    return false;
  }
  groupBuyRequestDispatching = recovery;
  return true;
}

export function finishCustomerGroupBuyRequestDispatch(
  recovery: CustomerGroupBuyRequestRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    groupBuyRequestDispatching !== recovery
  ) {
    return false;
  }
  groupBuyRequestDispatching = null;
  return true;
}

export function markCustomerGroupBuyRequestOutcomeUnknown(
  recovery: CustomerGroupBuyRequestRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateGroupBuyRequestRecovery() ||
    recovery !== groupBuyRequestRecovery ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state === "QUARANTINED"
  ) {
    return false;
  }
  const unknown = immutableClone({ ...recovery, state: "OUTCOME_UNKNOWN" as const });
  if (!persistGroupBuyRequestRecovery(unknown)) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  groupBuyRequestDispatching = null;
  groupBuyRequestRecovery = unknown;
  return true;
}

export function quarantineCustomerGroupBuyRequestRecovery(
  recovery: CustomerGroupBuyRequestRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateGroupBuyRequestRecovery() ||
    recovery !== groupBuyRequestRecovery ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity)
  ) {
    return false;
  }
  if (recovery.state === "QUARANTINED") return true;
  const quarantined = immutableClone({ ...recovery, state: "QUARANTINED" as const });
  if (!persistGroupBuyRequestRecovery(quarantined)) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  groupBuyRequestDispatching = null;
  groupBuyRequestRecovery = quarantined;
  return true;
}

function removeCustomerGroupBuyRequestRecovery(recovery: CustomerGroupBuyRequestRecovery): boolean {
  if (!persistGroupBuyRequestRecovery(null)) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  if (groupBuyRequestDispatching === recovery) groupBuyRequestDispatching = null;
  groupBuyRequestRecovery = null;
  return true;
}

export function clearCustomerGroupBuyRequestRecovery(
  recovery: CustomerGroupBuyRequestRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateGroupBuyRequestRecovery() ||
    recovery !== groupBuyRequestRecovery ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state !== "CLAIMED"
  ) {
    return false;
  }
  return removeCustomerGroupBuyRequestRecovery(recovery);
}

export function customerGroupBuyRequestResultMatchesRecovery(
  recovery: CustomerGroupBuyRequestRecovery,
  result: unknown,
): result is CustomerGroupBuyRequestMutationResult {
  return (
    groupBuyRequestRecoveryIsValid(recovery) &&
    recovery.state !== "QUARANTINED" &&
    isCustomerGroupBuyRequestMutationResult(result) &&
    result.operation === "CREATE" &&
    result.request.provider === recovery.request.provider &&
    result.request.voucherCodeSuffix === recovery.request.voucherCodeSuffix &&
    result.request.note === recovery.request.note
  );
}

export function customerGroupBuyRequestRecoveryCanQuery(
  recovery: CustomerGroupBuyRequestRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  return (
    identityIsValid(identity) &&
    hydrateGroupBuyRequestRecovery() &&
    recovery === groupBuyRequestRecovery &&
    recovery.ownerIdentityKey === customerSelfServiceIdentityKey(identity) &&
    recovery.state === "OUTCOME_UNKNOWN" &&
    groupBuyRequestStorageStillMatches()
  );
}

export function clearCustomerGroupBuyRequestAfterNotFound(
  recovery: CustomerGroupBuyRequestRecovery,
  identity: CustomerSelfServiceIdentity,
  lookupResult: CustomerGroupBuyRequestMutationResult | null,
): boolean {
  return (
    lookupResult === null &&
    customerGroupBuyRequestRecoveryCanQuery(recovery, identity) &&
    removeCustomerGroupBuyRequestRecovery(recovery)
  );
}

export function completeCustomerGroupBuyRequestRecovery(
  recovery: CustomerGroupBuyRequestRecovery,
  identity: CustomerSelfServiceIdentity,
  result: unknown,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateGroupBuyRequestRecovery() ||
    recovery !== groupBuyRequestRecovery ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state === "QUARANTINED"
  ) {
    return false;
  }
  if (!customerGroupBuyRequestResultMatchesRecovery(recovery, result)) {
    quarantineCustomerGroupBuyRequestRecovery(recovery, identity);
    return false;
  }
  return removeCustomerGroupBuyRequestRecovery(recovery);
}

function revokeRecoveryIsValid(value: unknown): value is CustomerVisitSessionRevokeRecovery {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["ownerIdentityKey", "identity", "request", "state"]) &&
    typeof value.ownerIdentityKey === "string" &&
    identityIsValid(value.identity) &&
    isCustomerVisitSessionRevokeRequest(value.request) &&
    value.ownerIdentityKey === customerSelfServiceIdentityKey(value.identity) &&
    (value.state === "CLAIMED" ||
      value.state === "OUTCOME_UNKNOWN" ||
      value.state === "QUARANTINED")
  );
}

function persistRevokeRecovery(recovery: CustomerVisitSessionRevokeRecovery | null): boolean {
  if (recovery === null) return removeVerified(CUSTOMER_SELF_SERVICE_REVOKE_RECOVERY_STORAGE_KEY);
  return writeVerified(CUSTOMER_SELF_SERVICE_REVOKE_RECOVERY_STORAGE_KEY, {
    version: 1,
    recovery,
  });
}

function hydrateRevoke(): boolean {
  if (revokeHydrated) return !revokeStorageUnavailable;
  revokeHydrated = true;
  const stored = readStored(CUSTOMER_SELF_SERVICE_REVOKE_RECOVERY_STORAGE_KEY);
  if (!stored.ok) {
    revokeStorageUnavailable = true;
    return false;
  }
  if (stored.value === null) return true;
  if (
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "recovery"]) ||
    stored.value.version !== 1 ||
    !revokeRecoveryIsValid(stored.value.recovery)
  ) {
    revokeStorageUnavailable = true;
    return false;
  }
  const restored = immutableClone({
    ...stored.value.recovery,
    state:
      stored.value.recovery.state === "CLAIMED"
        ? ("OUTCOME_UNKNOWN" as const)
        : stored.value.recovery.state,
  });
  if (stored.value.recovery.state === "CLAIMED" && !persistRevokeRecovery(restored)) {
    revokeStorageUnavailable = true;
    return false;
  }
  revokeRecovery = restored;
  return true;
}

function revokeStorageStillMatches(): boolean {
  const stored = readStored(CUSTOMER_SELF_SERVICE_REVOKE_RECOVERY_STORAGE_KEY);
  if (
    !stored.ok ||
    !isRecord(stored.value) ||
    !hasExactKeys(stored.value, ["version", "recovery"]) ||
    stored.value.version !== 1 ||
    !revokeRecoveryIsValid(stored.value.recovery) ||
    !revokeRecovery ||
    JSON.stringify(stored.value.recovery) !== JSON.stringify(revokeRecovery)
  ) {
    revokeStorageUnavailable = true;
    return false;
  }
  return true;
}

export function claimCustomerVisitSessionRevokeRecovery(
  identity: CustomerSelfServiceIdentity,
  request: CustomerVisitSessionRevokeRequest,
): CustomerSelfServiceRecoveryView<CustomerVisitSessionRevokeRecovery> {
  if (
    !identityIsValid(identity) ||
    !isCustomerVisitSessionRevokeRequest(request) ||
    !hydrateSession() ||
    !hydrateRevoke() ||
    !hydrateGroupBuyRequestRecovery() ||
    groupBuyRequestRecovery !== null
  ) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  if (
    !sessionState ||
    customerSelfServiceIdentityKey(sessionIdentity(sessionState.session)) !==
      customerSelfServiceIdentityKey(identity)
  ) {
    return sessionState ? { status: "OTHER_IDENTITY" } : { status: "NONE" };
  }
  const ownerIdentityKey = customerSelfServiceIdentityKey(identity);
  if (revokeRecovery) {
    return revokeRecovery.ownerIdentityKey === ownerIdentityKey
      ? { status: "OWNED", recovery: revokeRecovery }
      : { status: "OTHER_IDENTITY" };
  }
  const claimed = immutableClone({
    ownerIdentityKey,
    identity,
    request,
    state: "CLAIMED" as const,
  });
  if (!persistRevokeRecovery(claimed)) {
    revokeStorageUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  revokeRecovery = claimed;
  return { status: "OWNED", recovery: claimed };
}

export function readCustomerVisitSessionRevokeRecovery(
  identity: CustomerSelfServiceIdentity,
): CustomerSelfServiceRecoveryView<CustomerVisitSessionRevokeRecovery> {
  if (!identityIsValid(identity) || !hydrateRevoke()) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  if (!revokeRecovery) return { status: "NONE" };
  return revokeRecovery.ownerIdentityKey === customerSelfServiceIdentityKey(identity)
    ? { status: "OWNED", recovery: revokeRecovery }
    : { status: "OTHER_IDENTITY" };
}

export function beginCustomerVisitSessionRevokeDispatch(
  recovery: CustomerVisitSessionRevokeRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateRevoke() ||
    recovery !== revokeRecovery ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state === "QUARANTINED" ||
    !revokeStorageStillMatches() ||
    revokeDispatching !== null
  ) {
    return false;
  }
  revokeDispatching = recovery;
  return true;
}

export function finishCustomerVisitSessionRevokeDispatch(
  recovery: CustomerVisitSessionRevokeRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    revokeDispatching !== recovery
  ) {
    return false;
  }
  revokeDispatching = null;
  return true;
}

export function markCustomerVisitSessionRevokeOutcomeUnknown(
  recovery: CustomerVisitSessionRevokeRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateRevoke() ||
    recovery !== revokeRecovery ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state === "QUARANTINED"
  ) {
    return false;
  }
  const unknown = immutableClone({ ...recovery, state: "OUTCOME_UNKNOWN" as const });
  if (!persistRevokeRecovery(unknown)) {
    revokeStorageUnavailable = true;
    return false;
  }
  revokeDispatching = null;
  revokeRecovery = unknown;
  return true;
}

export function quarantineCustomerVisitSessionRevokeRecovery(
  recovery: CustomerVisitSessionRevokeRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateRevoke() ||
    recovery !== revokeRecovery ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity)
  ) {
    return false;
  }
  if (recovery.state === "QUARANTINED") return true;
  const quarantined = immutableClone({ ...recovery, state: "QUARANTINED" as const });
  if (!persistRevokeRecovery(quarantined)) {
    revokeStorageUnavailable = true;
    return false;
  }
  revokeDispatching = null;
  revokeRecovery = quarantined;
  return true;
}

export function customerVisitSessionRevokeResultMatchesRecovery(
  recovery: CustomerVisitSessionRevokeRecovery,
  result: unknown,
): result is CustomerVisitSessionRevokeResult {
  return (
    revokeRecoveryIsValid(recovery) &&
    recovery.state !== "QUARANTINED" &&
    isCustomerVisitSessionRevokeResult(result) &&
    result.session.tenantId === recovery.identity.tenantId &&
    result.session.storeId === recovery.identity.storeId &&
    result.session.visitId === recovery.identity.visitId
  );
}

export function customerVisitSessionIsExactlyInvalid(error: unknown): boolean {
  if (!isRecord(error)) return false;
  const status =
    typeof error.status === "number"
      ? error.status
      : typeof error.statusCode === "number"
        ? error.statusCode
        : null;
  return (
    status === 401 &&
    typeof error.code === "string" &&
    [
      "CUSTOMER_VISIT_AUTH_REQUIRED",
      "CUSTOMER_VISIT_SESSION_EXPIRED",
      "CUSTOMER_VISIT_SESSION_REVOKED",
    ].includes(error.code)
  );
}

function anyMutationRecovery(): boolean {
  return mutationRecoveries.CREATE !== null || mutationRecoveries.CANCEL !== null;
}

function clearLocalStateAfterResolvedRevoke(
  recovery: CustomerVisitSessionRevokeRecovery,
  identity: CustomerSelfServiceIdentity,
): boolean {
  const identityKey = customerSelfServiceIdentityKey(identity);
  if (
    recovery !== revokeRecovery ||
    recovery.ownerIdentityKey !== identityKey ||
    recovery.state === "QUARANTINED" ||
    !hydrateSession() ||
    !hydrateMutations() ||
    !hydrateGroupBuyRequestRecovery() ||
    groupBuyRequestRecovery !== null ||
    anyMutationRecovery() ||
    (sessionState !== null &&
      customerSelfServiceIdentityKey(sessionIdentity(sessionState.session)) !== identityKey)
  ) {
    return false;
  }
  if (!removeVerified(CUSTOMER_SELF_SERVICE_SESSION_STORAGE_KEY)) {
    sessionStorageUnavailable = true;
    return false;
  }
  if (!removeVerified(CUSTOMER_SELF_SERVICE_MUTATION_RECOVERY_STORAGE_KEY)) {
    mutationStorageUnavailable = true;
    return false;
  }
  if (!removeVerified(CUSTOMER_GROUP_BUY_REQUEST_RECOVERY_STORAGE_KEY)) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  const creation = readCustomerVisitSessionCreationRecovery({
    tenantId: identity.tenantId,
    storeId: identity.storeId,
  });
  if (creation.status === "OWNED" && !removeSessionCreationSecret()) return false;
  if (creation.status === "RECOVERY_STORAGE_UNAVAILABLE") return false;
  if (!persistRevokeRecovery(null)) {
    revokeStorageUnavailable = true;
    return false;
  }
  revokeRecovery = null;
  revokeDispatching = null;
  sessionState = null;
  sessionHydrated = true;
  mutationRecoveries = { CREATE: null, CANCEL: null };
  mutationsHydrated = true;
  dispatchingMutations.clear();
  groupBuyRequestRecovery = null;
  groupBuyRequestHydrated = true;
  groupBuyRequestDispatching = null;
  return true;
}

export function completeCustomerVisitSessionRevoke(
  recovery: CustomerVisitSessionRevokeRecovery,
  identity: CustomerSelfServiceIdentity,
  result: unknown,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateRevoke() ||
    recovery !== revokeRecovery ||
    !customerVisitSessionRevokeResultMatchesRecovery(recovery, result)
  ) {
    if (recovery === revokeRecovery && recovery.state !== "QUARANTINED") {
      quarantineCustomerVisitSessionRevokeRecovery(recovery, identity);
    }
    return false;
  }
  return clearLocalStateAfterResolvedRevoke(recovery, identity);
}

export function clearCustomerVisitSessionRevokeAfterConfirmedInvalid(
  recovery: CustomerVisitSessionRevokeRecovery,
  identity: CustomerSelfServiceIdentity,
  error: unknown,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateRevoke() ||
    recovery !== revokeRecovery ||
    recovery.state === "QUARANTINED" ||
    !customerVisitSessionIsExactlyInvalid(error)
  ) {
    return false;
  }
  return clearLocalStateAfterResolvedRevoke(recovery, identity);
}

export function clearCustomerVisitSessionRevokeAfterDefiniteNoEffect(
  recovery: CustomerVisitSessionRevokeRecovery,
  identity: CustomerSelfServiceIdentity,
  error: unknown,
): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateRevoke() ||
    recovery !== revokeRecovery ||
    recovery.ownerIdentityKey !== customerSelfServiceIdentityKey(identity) ||
    recovery.state === "QUARANTINED" ||
    !isRecord(error) ||
    typeof error.code !== "string" ||
    error.code.length < 1 ||
    customerVisitSessionIsExactlyInvalid(error) ||
    customerSelfServiceMutationOutcomeIsUnknown(error) ||
    !persistRevokeRecovery(null)
  ) {
    return false;
  }
  revokeRecovery = null;
  revokeDispatching = null;
  return true;
}

export function clearCustomerSelfServiceSession(identity: CustomerSelfServiceIdentity): boolean {
  if (
    !identityIsValid(identity) ||
    !hydrateSession() ||
    !hydrateMutations() ||
    !hydrateGroupBuyRequestRecovery() ||
    !hydrateRevoke() ||
    revokeRecovery !== null ||
    groupBuyRequestRecovery !== null ||
    anyMutationRecovery()
  ) {
    return false;
  }
  const identityKey = customerSelfServiceIdentityKey(identity);
  if (
    sessionState &&
    customerSelfServiceIdentityKey(sessionIdentity(sessionState.session)) !== identityKey
  ) {
    return false;
  }
  if (!removeVerified(CUSTOMER_SELF_SERVICE_SESSION_STORAGE_KEY)) {
    sessionStorageUnavailable = true;
    return false;
  }
  if (!removeVerified(CUSTOMER_SELF_SERVICE_MUTATION_RECOVERY_STORAGE_KEY)) {
    mutationStorageUnavailable = true;
    return false;
  }
  if (!removeVerified(CUSTOMER_GROUP_BUY_REQUEST_RECOVERY_STORAGE_KEY)) {
    groupBuyRequestStorageUnavailable = true;
    return false;
  }
  const creation = readCustomerVisitSessionCreationRecovery({
    tenantId: identity.tenantId,
    storeId: identity.storeId,
  });
  if (creation.status === "OWNED") {
    if (!removeSessionCreationSecret()) return false;
  } else if (creation.status === "RECOVERY_STORAGE_UNAVAILABLE") {
    return false;
  }
  sessionState = null;
  sessionHydrated = true;
  mutationRecoveries = { CREATE: null, CANCEL: null };
  mutationsHydrated = true;
  dispatchingMutations.clear();
  groupBuyRequestRecovery = null;
  groupBuyRequestHydrated = true;
  groupBuyRequestDispatching = null;
  return true;
}

export function customerSelfServiceMutationOutcomeIsUnknown(error: unknown): boolean {
  if (!isRecord(error)) return true;
  const code = typeof error.code === "string" ? error.code : null;
  if (
    code === "CUSTOMER_SELF_SERVICE_RESPONSE_INVALID" ||
    code === "CUSTOMER_SELF_SERVICE_GATEWAY_UNREACHABLE"
  ) {
    return true;
  }
  const status =
    typeof error.status === "number"
      ? error.status
      : typeof error.statusCode === "number"
        ? error.statusCode
        : null;
  if (status === null || !Number.isInteger(status) || status === 408 || status >= 500) return true;
  return !(status >= 400 && status < 500);
}

export const customerSelfServiceOutcomeIsUnknown = customerSelfServiceMutationOutcomeIsUnknown;

export function resetCustomerSelfServiceRecoveryForTests(options?: {
  preserveStorage?: boolean;
}): void {
  sessionCreationRecovery = null;
  sessionCreationHydrated = false;
  sessionCreationStorageUnavailable = false;
  sessionCreationDispatching = null;
  sessionState = null;
  sessionHydrated = false;
  sessionStorageUnavailable = false;
  mutationRecoveries = { CREATE: null, CANCEL: null };
  mutationsHydrated = false;
  mutationStorageUnavailable = false;
  dispatchingMutations.clear();
  revokeRecovery = null;
  revokeHydrated = false;
  revokeStorageUnavailable = false;
  revokeDispatching = null;
  groupBuyRequestRecovery = null;
  groupBuyRequestHydrated = false;
  groupBuyRequestStorageUnavailable = false;
  groupBuyRequestDispatching = null;
  if (!options?.preserveStorage) {
    const target = storage();
    try {
      target?.removeItem(CUSTOMER_SELF_SERVICE_SESSION_CREATION_RECOVERY_STORAGE_KEY);
      target?.removeItem(CUSTOMER_SELF_SERVICE_SESSION_STORAGE_KEY);
      target?.removeItem(CUSTOMER_SELF_SERVICE_MUTATION_RECOVERY_STORAGE_KEY);
      target?.removeItem(CUSTOMER_SELF_SERVICE_REVOKE_RECOVERY_STORAGE_KEY);
      target?.removeItem(CUSTOMER_GROUP_BUY_REQUEST_RECOVERY_STORAGE_KEY);
    } catch {
      sessionCreationStorageUnavailable = true;
      sessionStorageUnavailable = true;
      mutationStorageUnavailable = true;
      revokeStorageUnavailable = true;
      groupBuyRequestStorageUnavailable = true;
    }
  }
}
